/**
 * Explicit resource representations exchanged by negotiated V2 peers.
 *
 * A resource is deliberately not inferred from its serialized size. The
 * discriminator is part of the wire contract so callers can decide whether a
 * browser URL or an inline value is acceptable before native does any work.
 */

export type ResourceRepresentationKind = "url" | "base64";

export interface ResourceMetadata {
  mimeType: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
}

export interface UrlResourceRepresentation extends ResourceMetadata {
  kind: "url";
  url: string;
}

export interface Base64ResourceRepresentation extends ResourceMetadata {
  kind: "base64";
  base64: string;
}

export type ResourceRepresentation =
  | UrlResourceRepresentation
  | Base64ResourceRepresentation;

export interface ResourceRequest {
  resourceId: string;
  representations: ResourceRepresentationKind[];
}

export class InvalidResourceRepresentationError extends Error {
  constructor(message = "Invalid resource representation") {
    super(message);
    this.name = "InvalidResourceRepresentationError";
  }
}

export class UnsupportedResourceRepresentationError extends Error {
  constructor(message = "No requested resource representation is supported") {
    super(message);
    this.name = "UnsupportedResourceRepresentationError";
  }
}

const RESOURCE_KINDS: ResourceRepresentationKind[] = ["url", "base64"];

function assertSafeMetadata(value: unknown): asserts value is ResourceMetadata {
  if (!value || typeof value !== "object") {
    throw new InvalidResourceRepresentationError();
  }
  const metadata = value as Record<string, unknown>;
  if (typeof metadata.mimeType !== "string" || metadata.mimeType.trim().length === 0) {
    throw new InvalidResourceRepresentationError("Resource MIME type is required");
  }
  for (const key of ["width", "height", "sizeBytes"] as const) {
    const candidate = metadata[key];
    if (
      candidate !== undefined &&
      (!Number.isSafeInteger(candidate) || (candidate as number) < 0)
    ) {
      throw new InvalidResourceRepresentationError(
        `Resource ${key} must be a non-negative safe integer`,
      );
    }
  }
}

/** Validate and return a copy of an ordered representation preference list. */
export function validateResourceRequest(request: ResourceRequest): ResourceRequest {
  if (!request || typeof request.resourceId !== "string" || request.resourceId.length === 0) {
    throw new InvalidResourceRepresentationError("Resource identifier is required");
  }
  if (
    !Array.isArray(request.representations) ||
    request.representations.length === 0 ||
    request.representations.some((kind) => !RESOURCE_KINDS.includes(kind)) ||
    new Set(request.representations).size !== request.representations.length
  ) {
    throw new InvalidResourceRepresentationError(
      "Resource representations must be a non-empty ordered list",
    );
  }
  return {
    resourceId: request.resourceId,
    representations: [...request.representations],
  };
}

/** Decode the strict V2 tagged union. Legacy V1 shape is intentionally rejected. */
export function decodeResourceRepresentation(value: unknown): ResourceRepresentation {
  if (!value || typeof value !== "object") {
    throw new InvalidResourceRepresentationError();
  }
  const representation = value as Record<string, unknown>;
  const kind = representation.kind;
  if (kind !== "url" && kind !== "base64") {
    throw new InvalidResourceRepresentationError("Resource kind is invalid");
  }
  assertSafeMetadata(value);
  const hasUrl = Object.prototype.hasOwnProperty.call(representation, "url");
  const hasBase64 = Object.prototype.hasOwnProperty.call(representation, "base64");
  if (kind === "url") {
    if (!hasUrl || hasBase64 || typeof representation.url !== "string" || representation.url.length === 0) {
      throw new InvalidResourceRepresentationError("URL resource value is invalid");
    }
    return { ...representation } as unknown as UrlResourceRepresentation;
  }
  if (!hasBase64 || hasUrl || typeof representation.base64 !== "string") {
    throw new InvalidResourceRepresentationError("Base64 resource value is invalid");
  }
  return { ...representation } as unknown as Base64ResourceRepresentation;
}

/** Decode the legacy untagged V1 Base64 shape at a version-gated boundary. */
export function decodeLegacyBase64Resource(value: unknown): Base64ResourceRepresentation {
  if (!value || typeof value !== "object") {
    throw new InvalidResourceRepresentationError();
  }
  const legacy = value as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(legacy, "kind") || typeof legacy.base64 !== "string") {
    throw new InvalidResourceRepresentationError("Legacy Base64 resource value is invalid");
  }
  assertSafeMetadata(value);
  return { kind: "base64", ...legacy } as Base64ResourceRepresentation;
}
